import { useCallback } from 'react';

import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { type LastInteractedStore } from '../../../../context';
import { useCart } from '../../../../hooks';
import { type ProductForRestaurant } from '../../ProductDetails.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsNavigation = (params: Params) => {
  const { baseProductSlug, lastInteractedStore, productForRestaurant } = params;

  const deliveryAddressId = lastInteractedStore?.addressId;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { isCartEmpty } = useCart();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToMenu = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.dismissTo('/menu');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'MenuTab',
      params: { screen: 'MenuWithoutSlug' },
    });
  }, [isExpoRouterEnabled, router, navigation]);

  const navigateToLocationSearch = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.dismissTo('/menu/locations');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'MenuTab',
      params: { screen: 'Locations' },
    });
  }, [isExpoRouterEnabled, router, navigation]);

  const navigateToProductDetailsLocation = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate({
        pathname: '/product-details/[baseProductSlug]/change-location',
        params: { baseProductSlug },
      });
      return;
    }

    navigation?.navigate('Modal', {
      screen: 'ProductDetailsLocation',
      params: { baseProductSlug },
    });
  }, [isExpoRouterEnabled, baseProductSlug, navigation, router]);

  const navigateBack = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.back();
      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigateToMenu();
  }, [isExpoRouterEnabled, navigateToMenu, navigation, router]);

  const navigateToCustomization = useCallback(() => {
    const shouldSelectLocation = !lastInteractedStore || !productForRestaurant;

    if (shouldSelectLocation) {
      navigateToProductDetailsLocation();
      return;
    }

    if (isExpoRouterEnabled) {
      if (deliveryAddressId) {
        router?.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: {
            productSlug: productForRestaurant?.slug ?? '',
            addressId: deliveryAddressId,
          },
        });
      } else {
        router?.navigate({
          pathname: '/[restaurantSlug]/[productSlug]',
          params: {
            productSlug: productForRestaurant?.slug ?? '',
            restaurantSlug: productForRestaurant.restaurant.slug,
          },
        });
      }

      return;
    }

    if (deliveryAddressId) {
      navigation?.navigate('Modal', {
        screen: 'CustomizationDelivery',
        params: {
          productSlug: productForRestaurant?.slug,
          addressId: deliveryAddressId,
        },
      });

      return;
    }

    navigation?.navigate('Modal', {
      screen: 'Customization',
      params: {
        productSlug: productForRestaurant?.slug,
        restaurantSlug: productForRestaurant.restaurant.slug,
      },
    });
  }, [
    deliveryAddressId,
    isExpoRouterEnabled,
    lastInteractedStore,
    navigateToProductDetailsLocation,
    navigation,
    productForRestaurant,
    router,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateBack,
    navigateToCustomization,

    // NOTE: If there is no need for reorder, we can open "inline" location search.
    navigateToLocationSearch: isCartEmpty
      ? navigateToProductDetailsLocation
      : navigateToLocationSearch,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
  lastInteractedStore: LastInteractedStore | undefined;
  productForRestaurant: ProductForRestaurant | null | undefined;
};
